/**
 * <sk-chill-moment> — snippets/sk-chill-moment.liquid. Chills the can once, the
 * first time it is on screen. Visitors with reduced motion, or without this
 * script, see the chilled can.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const COUNT_DELAY_MS = 250;
const COUNT_MS = 2200;

class ChillMoment extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #frame = 0;

  connectedCallback() {
    if (this.#observer || reducedMotion.matches || !('IntersectionObserver' in window)) return;
    if (this.classList.contains('is-chilling')) return;
    const stage = this.querySelector('.skc__stage');
    if (!stage) return;

    this.classList.add('is-warm');
    this.#show(Number(this.dataset.from));

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#observer = null;
        this.#play();
      },
      { threshold: 0.6 },
    );
    this.#observer.observe(stage);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    cancelAnimationFrame(this.#frame);
    if (this.classList.contains('is-chilling')) this.#show(Number(this.dataset.to));
  }

  #play() {
    this.classList.replace('is-warm', 'is-chilling');
    const from = Number(this.dataset.from);
    const to = Number(this.dataset.to);
    if (!Number.isFinite(from) || !Number.isFinite(to)) return;
    const start = performance.now() + COUNT_DELAY_MS;

    const tick = (now) => {
      const progress = Math.min(1, Math.max(0, (now - start) / COUNT_MS));
      this.#show(Math.round(from + (to - from) * (1 - (1 - progress) ** 3)));
      if (progress < 1) this.#frame = requestAnimationFrame(tick);
    };
    this.#frame = requestAnimationFrame(tick);
  }

  #show(degrees) {
    const readout = this.querySelector('[data-skc-deg]');
    if (readout && Number.isFinite(degrees)) readout.textContent = String(degrees);
  }
}

if (!customElements.get('sk-chill-moment')) customElements.define('sk-chill-moment', ChillMoment);
